import { UUID } from "@bitory-gmbh/nidavellir";
import { backendApi } from "@/services/backend/api";
import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { Filter } from "@/shared/lib/filter/filter";
import { Lst } from "@/shared/lib/lst/lst";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { DataSheet, DataSheetPatch } from "../types/data-sheet";

export const omniDataSheetApi = backendApi
  .enhanceEndpoints({ addTagTypes: ["OmniDataSheet"] })
  .injectEndpoints({
    endpoints: (build) => ({
      omniDataSheetCreate: build.mutation<DataSheet, CreateRequest>({
        query: (request) => {
          const body = new FormData();
          body.append("id", request.id);
          body.append("companyId", request.companyId);
          body.append("serviceKind", request.serviceKind);
          body.append("label", request.label);
          if (request.file) {
            body.append("file", request.file, request.file.name);
          }
          return {
            url: "/omni/data-sheet",
            method: "POST",
            body,
            formData: true,
          };
        },
        invalidatesTags: [{ type: "OmniDataSheet", id: "LIST" }],
      }),
      omniDataSheetShow: build.query<DataSheet, ShowRequest>({
        query: (request) => ({
          url: "/omni/data-sheet",
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("OmniDataSheet"),
      }),
      omniDataSheetList: build.query<Lst<DataSheet>, ListRequest>({
        query: (request) => ({
          url: "/omni/data-sheet/list",
          method: "GET",
          params: base64ListRequest(request),
        }),
        providesTags: (result) => providesList(result?.items, "OmniDataSheet"),
      }),
      omniDataSheetPatch: build.mutation<DataSheet, PatchRequest>({
        query: (request) => ({
          url: "/omni/data-sheet",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "OmniDataSheet", id: request.id },
          { type: "OmniDataSheet", id: "LIST" },
        ],
      }),
      omniDataSheetDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: "/omni/data-sheet",
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: [{ type: "OmniDataSheet", id: "LIST" }],
      }),
      omniDataSheetFileDownload: build.mutation<
        { filename: string; objectURL: string },
        ShowRequest
      >({
        query: (request) => ({
          url: "/omni/data-sheet/file",
          method: "GET",
          params: { ...request, download: true },
          headers: { Accept: "application/pdf" },
          responseHandler: downloadResponseHandler,
          // Keep the PDF out of the browser's HTTP cache.
          cache: "no-store",
        }),
      }),
    }),
  });

export const {
  useOmniDataSheetCreateMutation,
  useOmniDataSheetShowQuery,
  useOmniDataSheetListQuery,
  useOmniDataSheetPatchMutation,
  useOmniDataSheetDeleteMutation,
  useOmniDataSheetFileDownloadMutation,
} = omniDataSheetApi;

/**
 * Mirrors `datasheet.CreateRequest`, backend/core/omni/domain/datasheet/service_port.go.
 * It is sent as a multipart form; the name of the file is its filename.
 */
export type CreateRequest = {
  id: UUID;
  companyId: UUID;
  serviceKind: string;
  label: string;
  file?: File;
};

/** Mirrors `datasheet.ShowRequest`, backend/core/omni/domain/datasheet/service_port.go. */
export type ShowRequest = {
  id: UUID;
};

/** Mirrors `datasheet.ListRequest`, backend/core/omni/domain/datasheet/service_port.go. */
export type ListRequest = {
  company: Filter<UUID>;
  serviceKind?: Filter<string>;
  active?: Filter<boolean>;
  id?: Filter<UUID>;
  orderBy?: OrdrBy<"id" | "label" | "createdAt" | "updatedAt">[];
  limit?: number;
  offset?: number;
};

/** Mirrors `datasheet.PatchRequest`, backend/core/omni/domain/datasheet/service_port.go. */
export type PatchRequest = DataSheetPatch & { id: UUID };

/** Mirrors `datasheet.DeleteRequest`, backend/core/omni/domain/datasheet/service_port.go. */
export type DeleteRequest = {
  id: UUID;
};
